
import { Component, Prop, Vue } from 'vue-property-decorator';
import { Getter } from 'vuex-class';
import { DateTime } from 'luxon';
import { computePercent, formatElapsed, formatTime, STATUS_COLORS } from '@/utils/bulkJob';
import { partition } from '@/utils/general';
import TableHeaders from '@/components/shared/suites/TableHeaders.vue';
import TextButton from '@/components/shared/suites/TextButton.vue';
import TextField from '@/components/shared/TextField.vue';
import WarningModal from '@/components/shared/suites/WarningModal.vue';

const STATUS_ORDER: Record<BulkJobLocationStatus, number> = {
	processing: 0,
	failed: 1,
	pending: 2,
	completed: 3,
	skipped: 4
};

@Component<BulkJobProgress>({
	components: {
		TableHeaders,
		TextButton,
		TextField,
		WarningModal
	}
})
export default class BulkJobProgress extends Vue {
	@Prop({ type: Object, required: true }) private job!: BulkJob;
	@Prop({ type: Boolean, required: false, default: true }) private cancellable!: boolean;
	@Prop({ type: String, required: false, default: '' }) private title!: string;
	@Prop({ type: Boolean, required: false, default: false }) private hasActiveJob!: boolean;
	@Prop({ type: Boolean, required: false, default: false }) private isCancelling!: boolean;
	@Prop({ type: String, required: false, default: 'bulk-job' }) private idPrefix!: string;
	@Getter('getRestaurants', { namespace: 'auth' }) private userRestaurants!: Tenant[] | null;
	@Getter('getTenantId', { namespace: 'auth' }) private currentTenantId!: string;

	private showCancelConfirm: boolean = false;
	private detailPanel: number | null = null;
	private sortBy: string = 'started';
	private sortDirection: 'asc' | 'desc' = 'asc';
	private searchQuery: string = '';
	private formatTime: typeof formatTime = formatTime;

	private get detailHeaders(): TableHeader[] {
		return [
			{ text: this.$t('shared.bulk_job.progress.columns.name'), name: 'name', sort: this.sortBy === 'name' ? this.sortDirection : 'asc' },
			{ text: this.$t('shared.bulk_job.progress.columns.status'), name: 'status', sort: this.sortBy === 'status' ? this.sortDirection : 'asc' },
			{ text: this.$t('shared.bulk_job.progress.columns.started_at'), name: 'started', sort: this.sortBy === 'started' ? this.sortDirection : 'asc' },
			{ text: this.$t('shared.bulk_job.progress.columns.duration'), name: 'duration', sort: this.sortBy === 'duration' ? this.sortDirection : 'asc' },
			{ text: this.$t('shared.bulk_job.progress.columns.error') }
		];
	}

	private get showCancel(): boolean {
		return this.cancellable && (this.job.status === 'pending' || this.job.status === 'processing');
	}

	private get progressPercent(): number {
		return computePercent(this.job);
	}

	private get availableRestaurantMap(): Map<string, Tenant> {
		return new Map((this.userRestaurants || []).map((r: Tenant) => [r.id, r]));
	}

	private get sourceName(): string {
		if (!this.job.sourceSchema) {
			return '';
		}
		return this.availableRestaurantMap.get(this.job.sourceSchema)?.name || '';
	}

	private get failedRestaurantIds(): string[] {
		return (this.job.locations || []).filter((l: BulkJobLocation) => l.status === 'failed').map((l: BulkJobLocation) => l.restaurantId);
	}

	private get showRetryFailed(): boolean {
		return !this.cancellable && !this.hasActiveJob && this.failedRestaurantIds.length > 0;
	}

	private get elapsedLabel(): string {
		return formatElapsed(this.job.startedAt, this.job.completedAt);
	}

	private get sortedLocations(): BulkJobLocation[] {
		const locations: BulkJobLocation[] = this.job.locations || [];
		const query: string = this.searchQuery.trim().toLowerCase();
		const filtered: BulkJobLocation[] = query ? locations.filter((l: BulkJobLocation) => l.name.toLowerCase().includes(query)) : locations;
		const dir: number = this.sortDirection === 'asc' ? 1 : -1;

		if (this.sortBy === 'name') {
			return [...filtered].sort((a, b) => a.name.localeCompare(b.name) * dir);
		}
		if (this.sortBy === 'status') {
			return [...filtered].sort((a, b) => ((STATUS_ORDER[a.status] ?? 99) - (STATUS_ORDER[b.status] ?? 99)) * dir);
		}
		if (this.sortBy === 'started') {
			// Rows without a startedAt (pending) always sort last regardless of direction
			const [withStart, pending] = partition(filtered, (l: BulkJobLocation) => !!l.startedAt);
			withStart.sort((a, b) => a.startedAt!.localeCompare(b.startedAt!) * dir);
			return [...withStart, ...pending];
		}
		if (this.sortBy === 'duration') {
			const [withStart, pending] = partition(filtered, (l: BulkJobLocation) => !!l.startedAt);
			withStart.sort((a, b) => (this.durationSeconds(a) - this.durationSeconds(b)) * dir);
			return [...withStart, ...pending];
		}

		// Default: by status priority, then alphabetical by name
		return [...filtered].sort((a, b) => {
			const aOrder: number = STATUS_ORDER[a.status] ?? 99;
			const bOrder: number = STATUS_ORDER[b.status] ?? 99;
			if (aOrder !== bOrder) {
				return aOrder - bOrder;
			}
			return a.name.localeCompare(b.name);
		});
	}

	/**
	 * Auto-expand when viewing a historical job; collapsed by default for active jobs
	 *
	 * @return {void}
	 */
	private created(): void {
		this.detailPanel = this.cancellable ? null : 0;
	}

	private canOpenLocation(restaurantId: string): boolean {
		return this.availableRestaurantMap.has(restaurantId);
	}

	private isCurrentLocation(restaurantId: string): boolean {
		return restaurantId === this.currentTenantId;
	}

	private publicMenuUrl(restaurantId: string): string {
		const tenant: Tenant | undefined = this.availableRestaurantMap.get(restaurantId);
		return tenant ? `${process.env.VUE_APP_MENU_DOMAIN}/${tenant.slug}` : '';
	}

	private durationSeconds(location: BulkJobLocation): number {
		if (!location.startedAt) {
			return 0;
		}
		const end: DateTime = location.completedAt ? DateTime.fromISO(location.completedAt) : DateTime.now();
		return Math.max(0, Math.round(end.diff(DateTime.fromISO(location.startedAt), 'seconds').seconds));
	}

	private onToggleSort(columnName: string): void {
		if (this.sortBy === columnName) {
			this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
		}
		else {
			this.sortBy = columnName;
			this.sortDirection = 'asc';
		}
	}

	private statusColor(status: BulkJobStatus | BulkJobLocationStatus): string {
		return STATUS_COLORS[status] || 'grey';
	}

	private formatDuration(startedAt: string | null, completedAt: string | null): string {
		return formatElapsed(startedAt, completedAt) || '—';
	}

	private confirmCancel(): void {
		this.$emit('cancel', this.job.jobId);
		this.showCancelConfirm = false;
	}
}
